import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StatCard } from "../shared";

/* ----------------------- Care network view ----------------------- */

export default function CareNetworkView() {
    const [data, setData] = useState({ items: [], counts: { caregiver: 0, volunteer: 0, new: 0 } });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let active = true;
        const run = async () => {
            try {
                const r = await api.get("/admin/care-network/requests");
                if (active) setData(r.data);
            } catch {
                if (active) toast.error("Couldn't load care network requests.");
            } finally {
                if (active) setLoading(false);
            }
        };
        run();
        return () => { active = false; };
    }, [refreshTick]);

    const reload = () => setRefreshTick((n) => n + 1);

    const updateStatus = async (id, status) => {
        try {
            await api.post(`/admin/care-network/requests/${id}/status`, { status });
            toast.success("Updated.");
            reload();
        } catch {
            toast.error("Couldn't update.");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this request? This can't be undone.")) return;
        try {
            await api.delete(`/admin/care-network/requests/${id}`);
            toast.success("Deleted.");
            reload();
        } catch {
            toast.error("Couldn't delete.");
        }
    };

    const items = (data.items || []).filter((it) => filter === "all" ? true : it.kind === filter);

    return (
        <section className="space-y-5" data-testid="admin-care-network">
            <header>
                <h1 className="text-3xl font-heading font-semibold tracking-tight">Care network requests</h1>
                <p className="mt-1 text-sm text-[#4C5551]">
                    Caregivers asking for a private caregiver or local volunteers. Reach out personally until the directory ships.
                </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="carenet-counts">
                <StatCard label="New (unactioned)" value={data.counts.new || 0} tone="coral" />
                <StatCard label="Private caregivers" value={data.counts.caregiver || 0} tone="teal" />
                <StatCard label="Volunteers" value={data.counts.volunteer || 0} tone="lavender" />
            </div>

            <div className="flex flex-wrap gap-2">
                {["all", "caregiver", "volunteer"].map((k) => (
                    <button
                        key={k}
                        onClick={() => setFilter(k)}
                        data-testid={`carenet-tab-${k}`}
                        className={`px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${filter === k
                            ? "bg-teal text-white border-teal"
                            : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                            }`}
                    >
                        {k === "all" ? "All" : k === "caregiver" ? "Caregivers" : "Volunteers"}
                    </button>
                ))}
            </div>

            {loading && <p className="text-[#4C5551]">Loading…</p>}
            {!loading && items.length === 0 && (
                <p className="ml-card p-6 text-[#4C5551] text-sm">No requests yet for this filter.</p>
            )}

            <div className="space-y-3" data-testid="carenet-list">
                {items.map((it) => (
                    <article
                        key={it.id}
                        data-testid={`carenet-row-${it.id}`}
                        className="ml-card p-5"
                    >
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                                <span className={`text-xs px-2.5 py-0.5 rounded-2xl font-medium ${it.kind === "caregiver" ? "bg-teal-soft text-teal-dark" : "bg-stage_late-light text-stage_late-dark"
                                    }`}>
                                    {it.kind}
                                </span>
                                <span className={`text-xs px-2.5 py-0.5 rounded-2xl font-medium ${it.status === "new" ? "bg-coral-light text-coral-dark" :
                                    it.status === "contacted" ? "bg-yellow-soft text-yellow-dark" :
                                        it.status === "matched" ? "bg-stage_early-light text-stage_early-dark" :
                                            "bg-cream-dark text-[#4C5551]"
                                    }`}>
                                    {it.status}
                                </span>
                                <span className="text-xs text-[#6E7A74]">{new Date(it.created_at).toLocaleString()}</span>
                            </div>
                            <button
                                onClick={() => remove(it.id)}
                                data-testid={`carenet-delete-${it.id}`}
                                className="text-xs text-emergency-dark hover:underline"
                            >
                                Delete
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                            {it.name && <p><span className="text-[#4C5551]">Name:</span> <strong>{it.name}</strong></p>}
                            {it.email && <p><span className="text-[#4C5551]">Email:</span> <a href={`mailto:${it.email}`} className="text-teal-dark underline">{it.email}</a></p>}
                            {it.phone && <p><span className="text-[#4C5551]">Phone:</span> <a href={`tel:${it.phone}`} className="text-teal-dark">{it.phone}</a></p>}
                            {it.zip && <p><span className="text-[#4C5551]">Zip:</span> {it.zip}</p>}
                            {it.hours_needed && <p className="sm:col-span-2"><span className="text-[#4C5551]">When:</span> {it.hours_needed}</p>}
                            {it.needs && it.needs.length > 0 && (
                                <p className="sm:col-span-2"><span className="text-[#4C5551]">Needs:</span> {it.needs.join(" · ")}</p>
                            )}
                        </div>
                        {it.notes && (
                            <p className="mt-2 text-sm bg-cream-dark/50 rounded-xl px-3 py-2 text-[#1F2624]">
                                &ldquo;{it.notes}&rdquo;
                            </p>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                            {["new", "contacted", "matched", "archived"]
                                .filter((s) => s !== it.status)
                                .map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => updateStatus(it.id, s)}
                                        data-testid={`carenet-status-${it.id}-${s}`}
                                        className="text-xs px-3 py-1 rounded-2xl border border-[#E2E8F0] text-[#4C5551] hover:bg-cream-dark"
                                    >
                                        Mark {s}
                                    </button>
                                ))}
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}


